import {
  ChangeDetectionStrategy,
  Component,
  InjectionToken,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { filter, map, firstValueFrom } from 'rxjs';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActingPostService } from '../../../core/auth/acting-post.service';
import { KeycloakService } from '../../../core/auth/keycloak.service';
import { ActingPost, ACTING_POST_SWITCH_TOAST_KEY } from '../../../core/auth/acting-post.model';
import { NotifyService } from '../../services/notify.service';
import { UnsavedWorkRegistry } from '../../../core/services/unsaved-work.registry';
import { I18nService } from '../../../core/services/i18n.service';
import { TranslatePipe } from '../../pipes/translate.pipe';

/** Testability seam: a spec cannot call the real window.location.reload()
 * without hanging the Karma browser. Override this token in tests instead. */
export const RELOAD_PAGE = new InjectionToken<() => void>('RELOAD_PAGE', {
  providedIn: 'root',
  factory: () => () => window.location.reload(),
});

/** Remembers a user-dragged pill position across reloads. Per-viewer only
 * (localStorage), never synced — dragging is a personal screen-layout
 * preference, not app state. */
const DRAG_POSITION_KEY = 'rkump_acting_switcher_position';

/** Below this many pixels of pointer movement, a press-release is still
 * treated as a click (opens the menu) rather than a drag. */
const DRAG_THRESHOLD_PX = 4;

interface PillPosition {
  left: number;
  top: number;
}

@Component({
  selector: 'app-acting-post-switcher',
  standalone: true,
  imports: [CommonModule, MatMenuModule, MatIconModule, MatTooltipModule, TranslatePipe],
  templateUrl: './acting-post-switcher.component.html',
  styleUrl: './acting-post-switcher.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActingPostSwitcherComponent implements OnInit {
  protected readonly actingPost = inject(ActingPostService);
  private readonly keycloak = inject(KeycloakService);
  private readonly notify = inject(NotifyService);
  private readonly unsavedWork = inject(UnsavedWorkRegistry);
  private readonly reloadPage = inject(RELOAD_PAGE);
  private readonly router = inject(Router);
  private readonly i18n = inject(I18nService);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  protected readonly visible = computed(
    () =>
      this.actingPost.featureEnabled() &&
      this.keycloak.isLoggedIn() &&
      this.actingPost.confirmed() &&
      !this.url().startsWith('/login') &&
      this.url() !== '/' &&
      this.actingPost.postings().length > 1
  );

  /** null = default bottom-right corner (the .acting-post-switcher CSS rule);
   * once the user drags it, this holds the explicit viewport position. */
  protected readonly position = signal<PillPosition | null>(this.readStoredPosition());
  protected readonly dragging = signal(false);

  private dragOrigin: { pointerX: number; pointerY: number; left: number; top: number } | null = null;
  private dragMoved = false;
  private readonly onPointerMove = (event: PointerEvent): void => this.handlePointerMove(event);
  private readonly onPointerUp = (): void => this.handlePointerUp();

  ngOnInit(): void {
    // Mounted once at the root (outside the router-outlet), so this runs
    // once per page load rather than per navigation — the same load the
    // picker triggers on the login path, needed here too because a user who
    // is already confirmed and reloads straight into a module never passes
    // through the picker.
    this.actingPost.loadPostings();
  }

  protected ariaLabel(posting: ActingPost): string {
    return this.i18n.instant('actingPost.switcher.optionLabel', {
      name: posting.displayName,
      path: posting.path,
    });
  }

  protected async onSelect(posting: ActingPost): Promise<void> {
    if (posting.nodeCd === this.actingPost.activeNodeCd()) {
      return; // clicking the active posting just closes the menu
    }

    const dirty = this.unsavedWork.checkUnsaved();
    const confirmed = await this.notify.confirm({
      title: this.i18n.instant('actingPost.switch.confirmTitle'),
      message: dirty
        ? this.i18n.instant('actingPost.switch.confirmMessageUnsaved', { posting: posting.displayName })
        : this.i18n.instant('actingPost.switch.confirmMessage', { posting: posting.displayName }),
      confirmText: this.i18n.instant('actingPost.switch.confirmBtn'),
      cancelText: this.i18n.instant('common.cancel'),
      icon: dirty ? 'warning' : 'sync_alt',
      iconColor: dirty ? 'warn' : 'primary',
    });
    if (!confirmed) {
      return; // Cancel/Escape/backdrop: no side effects at all
    }

    try {
      await firstValueFrom(this.actingPost.setActingPost(posting.nodeCd));
      // Success: keep switching() true — a UI that's about to be torn down
      // by the reload shouldn't re-enable and invite a double click. Write
      // the one-shot toast key so the next boot can say "You are now acting
      // as X"; leave the confirmed flag intact so the guard passes post-reload.
      try {
        sessionStorage.setItem(ACTING_POST_SWITCH_TOAST_KEY, posting.displayName);
      } catch {
        // ignore — the toast is a nicety, not load-bearing
      }
      this.reloadPage();
    } catch (err) {
      // No reload on failure — nothing local changed, so the pill still
      // shows the true posting.
      const httpErr = err as HttpErrorResponse;
      if (httpErr?.status === 403) {
        // The switch endpoint's 403 carries a `code` distinguishing "node no
        // longer in your assignment" from "the node's role override points at
        // a role you don't hold" — surface the more specific message when the
        // backend sent it, else fall back to the generic "no longer assigned"
        // wording (spec §8.5).
        const code = (httpErr.error as { code?: string } | null)?.code;
        const fallback =
          code === 'ROLE_NOT_ENTITLED'
            ? this.i18n.instant('actingPost.switch.roleNotEntitled')
            : this.i18n.instant('actingPost.switch.noLongerAssigned');
        this.notify.errorFrom(err, fallback);
        this.actingPost.loadPostings();
        if (!this.actingPost.postings().some((p) => p.nodeCd === this.actingPost.activeNodeCd())) {
          this.actingPost.clearConfirmed();
          this.router.navigateByUrl('/login/posting');
        }
      } else if (httpErr?.status === 401) {
        // AuthInterceptor already handles refresh/redirect for a 401.
      } else {
        this.notify.errorFrom(err, this.i18n.instant('actingPost.switch.failed'));
      }
    }
  }

  /** Starts a potential drag. Left mouse button / primary touch/pen only —
   * doesn't call preventDefault() here, so a plain click still reaches
   * matMenuTriggerFor normally when the pointer never moves past the
   * threshold (see handlePointerMove). */
  protected onPointerDown(event: PointerEvent, host: HTMLElement): void {
    if (event.button !== 0) {
      return;
    }
    const rect = host.getBoundingClientRect();
    this.dragOrigin = { pointerX: event.clientX, pointerY: event.clientY, left: rect.left, top: rect.top };
    this.dragMoved = false;
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp, { once: true });
    window.addEventListener('pointercancel', this.onPointerUp, { once: true });
  }

  private handlePointerMove(event: PointerEvent): void {
    if (!this.dragOrigin) {
      return;
    }
    const dx = event.clientX - this.dragOrigin.pointerX;
    const dy = event.clientY - this.dragOrigin.pointerY;

    if (!this.dragMoved) {
      if (Math.abs(dx) < DRAG_THRESHOLD_PX && Math.abs(dy) < DRAG_THRESHOLD_PX) {
        return; // still within click tolerance — not a drag yet
      }
      this.dragMoved = true;
      this.dragging.set(true);
    }

    event.preventDefault(); // stop touch-scroll once an actual drag is underway
    this.position.set(this.clampToViewport(this.dragOrigin.left + dx, this.dragOrigin.top + dy));
  }

  private handlePointerUp(): void {
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);

    if (this.dragMoved) {
      this.savePosition(this.position());
      // The browser dispatches a synthetic `click` right after this pointerup
      // — preventDefault/stopPropagation on the pointerup event itself does
      // NOT cancel that separate, later click event. Consume it explicitly
      // instead (capture phase, so it runs before matMenuTriggerFor's own
      // click listener), then clean up on the next macrotask.
      window.addEventListener('click', this.suppressGhostClick, { capture: true, once: true });
      setTimeout(() => window.removeEventListener('click', this.suppressGhostClick, { capture: true }), 0);
    }
    this.dragOrigin = null;
    this.dragMoved = false;
    this.dragging.set(false);
  }

  private readonly suppressGhostClick = (event: MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
  };

  private clampToViewport(left: number, top: number): PillPosition {
    const margin = 8;
    const maxLeft = Math.max(margin, window.innerWidth - margin - 56);
    const maxTop = Math.max(margin, window.innerHeight - margin - 44);
    return {
      left: Math.min(Math.max(margin, left), maxLeft),
      top: Math.min(Math.max(margin, top), maxTop),
    };
  }

  private readStoredPosition(): PillPosition | null {
    try {
      const raw = localStorage.getItem(DRAG_POSITION_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (typeof parsed?.left === 'number' && typeof parsed?.top === 'number') {
        return parsed;
      }
    } catch {
      // ignore — fall back to the default corner
    }
    return null;
  }

  private savePosition(pos: PillPosition | null): void {
    try {
      if (pos) {
        localStorage.setItem(DRAG_POSITION_KEY, JSON.stringify(pos));
      }
    } catch {
      // storage unavailable (private browsing etc.) — position just won't
      // survive a reload, which is a harmless degradation
    }
  }
}
